import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiCall } from '../../api/apiConfig';

// Async thunk for fetching chunked text (Manual)
export const fetchChunkedText = createAsyncThunk(
  'textChunker/fetchChunkedText',
  async ({ text, filters }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/split-text', 'POST', { text, filters });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('An unexpected error occurred while splitting text.');
    }
  }
);

// Async thunk for generating AI threads (Smart)
export const generateThread = createAsyncThunk(
  'textChunker/generateThread',
  async ({ text, platform, tone, format }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/generate-thread', 'POST', { text, platform, tone, format });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error generating thread.');
    }
  }
);

const textChunkerSlice = createSlice({
  name: 'textChunker',
  initialState: {
    chunks: [], // Array of chunked text
    chunkSize: 10,
    numberingFormat: '1/5',
    loading: false,
    error: null,
  },
  reducers: {
    updateChunks: (state, action) => {
      state.chunks = action.payload;
    },
    setChunkSize: (state, action) => {
      state.chunkSize = action.payload;
    },
    setNumberingFormat: (state, action) => {
      state.numberingFormat = action.payload;
    },
    clearChunks: (state) => {
      state.chunks = [];
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChunkedText.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChunkedText.fulfilled, (state, action) => {
        state.loading = false;
        state.chunks = Array.isArray(action.payload.chunks) ? action.payload.chunks : [];
      })
      .addCase(fetchChunkedText.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to split text into chunks.';
      })
      .addCase(generateThread.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateThread.fulfilled, (state, action) => {
        state.loading = false;
        state.chunks = Array.isArray(action.payload.posts) ? action.payload.posts : [];
      })
      .addCase(generateThread.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to generate thread.';
      });
  },
});

// Reusable selectors
export const selectChunks = (state) => state.textChunker.chunks;
export const selectChunkSize = (state) => state.textChunker.chunkSize;
export const selectNumberingFormat = (state) => state.textChunker.numberingFormat;
export const selectLoading = (state) => state.textChunker.loading;
export const selectError = (state) => state.textChunker.error;

export const { updateChunks, setChunkSize, setNumberingFormat, clearChunks } = textChunkerSlice.actions;

export default textChunkerSlice.reducer;
